import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ClipboardList, Download, Loader2, LayoutGrid, SlidersHorizontal, Table, Columns, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import ProductionFilterSelect from "@/components/ProductionFilterSelect";
import { receiptOptions, updateProductionFilter } from "./productionFilterOptions";
import DailyDispatch from "@/components/supplychain/DailyDispatch";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/production-report/daily`;
const localDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
export const initialSelection = () => ({ posting_date_from: localDate(), posting_date_to: localDate(), plants: "", process_orders: "", materials: "", storage_locations: "", movement_types: "101,102", batch: "", company_code: "", include_cancelled: false, include_non_order: false });
export const formatQuantity = value => value == null ? "—" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
export function validateSelection(selection) {
  if (!selection.posting_date_from || !selection.posting_date_to) return "Select both posting dates.";
  if (selection.posting_date_from > selection.posting_date_to) return "Posting Date From must not be after Posting Date To.";
  return "";
}

const DETAIL = [["posting_date", "GR Date"], ["plant", "Plant"], ["process_order", "Process Order"], ["order_type", "Order Type"], ["material", "Material"], ["material_description", "Material Description"], ["batch", "Batch"], ["quantity", "GR Qty", "quantity"], ["base_unit", "UoM"], ["storage_location", "Storage Location"], ["material_document", "Material Document"], ["material_document_year", "Year"], ["material_document_item", "Item"], ["goods_movement_type", "Movement Type"], ["cancelled", "Cancelled", "boolean"]];
const DAILY = [["posting_date", "GR Date"], ["plant", "Plant"], ["material", "Material"], ["material_description", "Material Description"], ["quantity", "Total GR Qty", "quantity"], ["base_unit", "UoM"], ["order_count", "# of Orders"], ["posting_count", "# of GR Postings"], ["running_quantity", "Running GR Qty", "quantity"]];
const PERIOD = [["period_from", "Period From"], ["period_to", "Period To"], ["plant", "Plant"], ["material", "Material"], ["material_description", "Material Description"], ["quantity", "Cumulative GR Qty", "quantity"], ["base_unit", "UoM"], ["target_quantity", "Target Qty", "quantity"], ["percent_of_target", "% of Target", "percent"]];

function ReportTable({ rows, columns, label }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);
  const pageCount = Math.max(1, Math.ceil(rows.length / 100));
  return <Card>
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label={label}>
        <thead className="bg-muted/60"><tr>{columns.map(([key, title]) => <th key={key} scope="col" className="px-4 py-3 text-left whitespace-nowrap font-semibold">{title}</th>)}</tr></thead>
        <tbody>{rows.slice(page * 100, (page + 1) * 100).map((row, index) => <tr key={row.id || index} className="border-t even:bg-muted/20">{columns.map(([key, , kind]) => <td key={key} className={`px-4 py-3 ${kind === "quantity" || kind === "percent" ? "text-right tabular-nums" : "text-left"} ${key === "material_description" ? "min-w-[200px]" : "whitespace-nowrap"}`}>
          {kind === "quantity" ? formatQuantity(row[key]) : kind === "percent" ? row[key] == null ? "—" : `${(Number(row[key]) * 100).toFixed(1)}%` : kind === "boolean" ? row[key] ? "Yes" : "No" : row[key] || "—"}
        </td>)}</tr>)}</tbody>
      </table>
    </div>
    <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-sm">
      <span>{rows.length} rows · Page {Math.min(page + 1, pageCount)} of {pageCount}</span>
      <div className="flex gap-2"><Button variant="outline" size="sm" disabled={!page} onClick={() => setPage(p => p - 1)}>Previous</Button><Button variant="outline" size="sm" disabled={page + 1 >= pageCount} onClick={() => setPage(p => p + 1)}>Next</Button></div>
    </div>
  </Card>;
}

const PLANT_NAMES = {
  "1100": "Doddaballapura",
  "1200": "Malur-Unit1",
  "1300": "Malur-unit 2",
  "1400": "Morbi WH",
};

function ReportTiles({ rows, columns, label }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);
  const pageSize = 24;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentRows = rows.slice(page * pageSize, (page + 1) * pageSize);

  const formatVal = (row, colKey, kind) => {
    const val = row[colKey];
    if (kind === "quantity") return formatQuantity(val);
    if (kind === "percent") return val == null ? "—" : `${(Number(val) * 100).toFixed(1)}%`;
    if (kind === "boolean") return val ? "Yes" : "No";
    if (colKey === "plant") {
      const plantName = row.plant_name || row.plant_description || PLANT_NAMES[val];
      return plantName ? `${val} (${plantName})` : (val || "—");
    }
    if (colKey === "material") {
      const matDesc = row.material_description;
      return matDesc ? `${val} (${matDesc})` : (val || "—");
    }
    return val || "—";
  };

  const qtyCol = columns.find(([key, , kind]) => kind === "quantity" || key === "quantity");

  return (
    <div className="space-y-4" aria-label={label}>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {currentRows.map((row, index) => {
          const mainTitle = row.material_description || row.material || `Record #${page * pageSize + index + 1}`;
          const mainQty = qtyCol ? formatVal(row, qtyCol[0], "quantity") : null;
          const unit = row.base_unit || "";
          const detailFields = columns.filter(([key]) => key !== "material_description" && key !== (qtyCol ? qtyCol[0] : "") && key !== "base_unit");

          return (
            <Card key={row.id || index} className="shadow-xs hover:shadow-md transition-shadow border bg-card flex flex-col justify-between">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground line-clamp-1" title={mainTitle}>
                    {mainTitle}
                  </p>
                  {row.material && row.material !== mainTitle && (
                    <p className="text-[11px] text-muted-foreground font-mono mt-0.5">
                      Code: {row.material}
                    </p>
                  )}
                </div>

                {mainQty && (
                  <div className="bg-primary/5 rounded-lg p-3 border border-primary/10">
                    <p className="text-[11px] text-muted-foreground font-medium uppercase">
                      {qtyCol ? qtyCol[1] : "Quantity"}
                    </p>
                    <p className="text-xl font-bold tracking-tight text-primary mt-0.5">
                      {mainQty} <span className="text-xs font-normal text-muted-foreground">{unit}</span>
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs pt-2 border-t border-border/40">
                  {detailFields.slice(0, 6).map(([key, labelText, kind]) => (
                    <div key={key} className="overflow-hidden">
                      <span className="text-[10px] text-muted-foreground block truncate">{labelText}</span>
                      <span className="font-medium text-foreground truncate block" title={String(row[key] || "")}>
                        {formatVal(row, key, kind)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-3 border-t pt-3 text-sm text-muted-foreground">
        <span>{rows.length} records · Page {Math.min(page + 1, pageCount)} of {pageCount}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={!page} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page + 1 >= pageCount} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}

async function errorMessage(error, fallback) {
  let data = error.response?.data;
  if (data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch { return fallback; }
  }
  return typeof data?.detail === "string" ? data.detail : fallback;
}

export default function DailyProductionReport() {
  const { user } = useAuth();
  const userKey = user?.username ? `_${user.username}` : "";
  const storageKey = `daily_prod_report_detail_cols${userKey}`;

  const [filters, setFilters] = useState(initialSelection);
  const [mainTab, setMainTab] = useState("production");
  const [applied, setApplied] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [exportNotice, setExportNotice] = useState("");
  const [tab, setTab] = useState("detail");
  const [viewMode, setViewMode] = useState("tiles");
  const [lookupRecords, setLookupRecords] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupWarnings, setLookupWarnings] = useState([]);
  const [lookupRetry, setLookupRetry] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState(initialSelection);
  const [visibleDetailKeys, setVisibleDetailKeys] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : DETAIL.slice(0, 11).map(c => c[0]);
    } catch {
      return DETAIL.slice(0, 11).map(c => c[0]);
    }
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        setVisibleDetailKeys(JSON.parse(saved));
      }
    } catch {}
  }, [storageKey]);

  const toggleDetailColumn = (key) => {
    setVisibleDetailKeys(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const resetDetailColumns = () => {
    const defaultKeys = DETAIL.slice(0, 11).map(c => c[0]);
    setVisibleDetailKeys(defaultKeys);
    try { localStorage.setItem(storageKey, JSON.stringify(defaultKeys)); } catch {}
  };

  const activeDetailColumns = DETAIL.filter(col => visibleDetailKeys.includes(col[0]));

  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const change = (key, value) => setFilters(previous => updateProductionFilter(previous, key, value));
  const changeDraft = (key, value) => setFilterDraft(previous => updateProductionFilter(previous, key, value));
  const openAdvancedFilters = () => { setFilterDraft(filters); setFiltersOpen(true); };
  const resetAdvancedFilters = () => setFilterDraft({
    ...initialSelection(),
    posting_date_from: filters.posting_date_from,
    posting_date_to: filters.posting_date_to,
  });
  const applyAdvancedFilters = () => { setFilters(filterDraft); setFiltersOpen(false); };
  const dirty = applied && JSON.stringify(applied) !== JSON.stringify(filters);

  useEffect(() => {
    const request = new AbortController();
    setLookupRecords([]); setLookupError(""); setLookupWarnings([]);
    if (!filters.posting_date_from || !filters.posting_date_to || filters.posting_date_from > filters.posting_date_to) {
      setLookupLoading(false);
      setLookupError("Choose a valid posting period to load SAP choices.");
      return () => request.abort();
    }
    setLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/filter-options`, { params: {
          posting_date_from: filters.posting_date_from, posting_date_to: filters.posting_date_to,
          movement_types: filters.movement_types,
        }, signal: request.signal });
        if (!request.signal.aborted) { setLookupRecords(data.records || []); setLookupWarnings(data.warnings || []); }
      } catch (failure) {
        if (!request.signal.aborted) setLookupError("SAP choices are unavailable. Retry, or enter codes manually.");
      } finally { if (!request.signal.aborted) setLookupLoading(false); }
    }, 350);
    return () => { clearTimeout(timer); request.abort(); };
  }, [filters.posting_date_from, filters.posting_date_to, filters.movement_types, lookupRetry]);

  async function load(event) {
    event.preventDefault();
    const invalid = validateSelection(filters);
    if (invalid) { setError(invalid); return; }
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    const selection = { ...filters };
    setLoading(true); setError(""); setReport(null); setApplied(null); setExportNotice("");
    try {
      const { data } = await axios.get(API, { params: selection, signal: request.signal });
      if (!request.signal.aborted) { setReport(data); setApplied(selection); }
    } catch (failure) {
      if (!request.signal.aborted) setError(await errorMessage(failure, "Unable to load the production report. Check your SAP connection and try again."));
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }

  async function download() {
    if (!applied || dirty || loading) return;
    setExporting(true); setError(""); setExportNotice("");
    try {
      const { data } = await axios.get(`${API}/export`, { params: applied, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Daily_Production_Report_${applied.posting_date_from}_${applied.posting_date_to}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
      setExportNotice("Downloaded all four sheets using the applied filters. Export reads fresh SAP data; new postings since your last refresh may appear.");
    } catch (failure) {
      setError(await errorMessage(failure, "Export failed. No report was downloaded."));
    } finally { setExporting(false); }
  }

  const dateFields = [["posting_date_from", "Posting Date From"], ["posting_date_to", "Posting Date To"]];
  const advancedFields = [["plants", "Plant(s)"], ["process_orders", "Process Order(s)"], ["materials", "Material(s)"], ["storage_locations", "Storage Location(s)"], ["batch", "Batch"], ["company_code", "Company Code"]];
  return <div className="space-y-6" data-testid="production-report-page">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ClipboardList className="text-primary" />Daily Report
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {mainTab === "production" ? "Goods receipts from process orders · net movements 101 / 102" : "SAP outbound goods dispatch & vehicle delivery tracking"}
        </p>
      </div>
      {mainTab === "production" && (
        <Button variant="outline" onClick={download} disabled={!report || dirty || loading || exporting} data-testid="export-prod-report-btn">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Export Excel
        </Button>
      )}
    </div>

    {/* Sub-navigation tab bar for Daily Production & Daily Dispatch */}
    <div className="border-b border-slate-200 pb-3" data-testid="daily-report-nav-bar">
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md bg-slate-100 p-1 rounded-xl" data-testid="daily-report-tabs">
          <TabsTrigger value="production" className="flex items-center justify-center gap-2 text-xs font-bold" data-testid="tab-daily-production">
            <ClipboardList className="h-4 w-4 text-blue-600" />
            Daily Production (GR)
          </TabsTrigger>
          <TabsTrigger value="dispatch" className="flex items-center justify-center gap-2 text-xs font-bold" data-testid="tab-daily-dispatch">
            <Truck className="h-4 w-4 text-emerald-600" />
            Daily Dispatch
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    {mainTab === "dispatch" ? <DailyDispatch /> : <div className="space-y-6">
    <Card className="max-w-6xl shadow-xs"><CardContent className="p-4"><form onSubmit={load} className="space-y-3">
      <fieldset disabled={loading || exporting} className="flex flex-wrap items-end gap-2.5">
        {dateFields.map(([key, label]) => <div key={key} className="w-full space-y-1 sm:w-48"><label htmlFor={key} className="text-xs font-medium block truncate">{label} *</label><Input id={key} type="date" value={filters[key]} required onChange={e => change(key, e.target.value)} className="h-9 text-xs px-2" /></div>)}
        <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
          <Button type="button" variant="outline" size="sm" className="h-9 gap-2" onClick={openAdvancedFilters} data-testid="all-production-filters-btn"><SlidersHorizontal className="h-4 w-4" />All Filters</Button>
          <Button type="submit" disabled={loading || exporting} size="sm" className="h-9 px-4 text-xs font-semibold" data-testid="go-prod-report-btn">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Go"}
          </Button>
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground border-t pt-2 mt-2" role="status"><span>{lookupLoading ? "Loading choices from SAP…" : lookupError || "Dropdowns show receipts in period. Use same date for daily report, or choose custom period."}</span>{lookupError && <Button type="button" size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={loading || exporting} onClick={() => setLookupRetry(value => value + 1)}>Retry choices</Button>}</div>
      {lookupWarnings.map((warning, index) => <p key={index} className="text-xs text-amber-700 dark:text-amber-400">{warning}</p>)}
    </form></CardContent></Card>
    <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl" data-testid="production-filters-dialog">
        <DialogHeader>
          <DialogTitle>All Production Filters</DialogTitle>
          <DialogDescription>Refine the report without changing its posting-date range.</DialogDescription>
        </DialogHeader>
        <fieldset disabled={loading || exporting} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {advancedFields.map(([key, label]) => <ProductionFilterSelect key={key} id={key} label={label} value={filterDraft[key]} onChange={value => changeDraft(key, value)} options={receiptOptions(lookupRecords, filterDraft, key)} multiple={["plants", "process_orders", "materials", "storage_locations"].includes(key)} loading={lookupLoading} error={lookupError} disabled={loading || exporting} />)}
          <div className="space-y-1 min-w-0"><label id="movement-label" className="text-xs font-medium block truncate">Movement Types</label><Select value={filterDraft.movement_types} onValueChange={value => changeDraft("movement_types", value)} disabled={loading || exporting}><SelectTrigger aria-labelledby="movement-label" className="h-9 text-xs px-2"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="101,102">101 + 102 · Net receipts</SelectItem><SelectItem value="101">101 · Receipts only</SelectItem><SelectItem value="102">102 · Reversals only</SelectItem></SelectContent></Select></div>
          <div className="flex flex-wrap items-center gap-5 pt-5">
            <div className="flex items-center gap-2"><Checkbox id="include-cancelled" checked={filterDraft.include_cancelled} onCheckedChange={value => changeDraft("include_cancelled", value === true)} disabled={loading || exporting} /><label htmlFor="include-cancelled" className="text-xs font-medium cursor-pointer">Include cancelled pairs</label></div>
            <div className="flex items-center gap-2"><Checkbox id="include-non-order" checked={filterDraft.include_non_order} onCheckedChange={value => changeDraft("include_non_order", value === true)} disabled={loading || exporting} /><label htmlFor="include-non-order" className="text-xs font-medium cursor-pointer">Include non-order receipts</label></div>
          </div>
        </fieldset>
        <DialogFooter className="gap-2 border-t pt-4 sm:justify-between sm:space-x-0">
          <Button type="button" variant="ghost" onClick={resetAdvancedFilters} disabled={loading || exporting}>Reset</Button>
          <div className="flex gap-2"><Button type="button" variant="outline" onClick={() => setFiltersOpen(false)} disabled={loading || exporting}>Cancel</Button><Button type="button" onClick={applyAdvancedFilters} disabled={loading || exporting}>Apply Filters</Button></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {error && <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>}
    {dirty && <p role="status" className="text-sm text-amber-700 dark:text-amber-400">Filters changed. Click Go to refresh the report before exporting.</p>}
    {exportNotice && <p role="status" className="text-sm text-muted-foreground">{exportNotice}</p>}
    {!report && !loading && !error && <Card><CardContent className="py-12 text-center text-muted-foreground">Select a posting period and click Go. No SAP data has been loaded yet.</CardContent></Card>}
    {report && <>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs max-w-6xl"><span className="font-medium text-emerald-700 dark:text-emerald-400">Live SAP · {applied.posting_date_from} to {applied.posting_date_to}</span><span className="text-muted-foreground">Generated {new Date(report.generated_at).toLocaleString()}</span></div>
      {report.warnings.map((warning, index) => <p role="status" key={index} className="rounded-lg border border-amber-300/50 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-200 max-w-6xl">{warning}</p>)}
      {report.hidden_cancelled_items > 0 && <p className="text-xs text-muted-foreground max-w-6xl">{report.hidden_cancelled_items} same-day cancelled items hidden; net quantities are unchanged.</p>}
      <div className="grid gap-3 sm:grid-cols-3 max-w-6xl">
        <Card className="shadow-xs"><CardContent className="p-3"><p className="text-xs font-medium text-muted-foreground">Net received quantity</p><div className="mt-1 text-lg font-bold text-foreground">{report.summary.totals_by_unit.length ? report.summary.totals_by_unit.map(total => <p key={total.unit}>{formatQuantity(total.quantity)} {total.unit}</p>) : "No receipts"}</div></CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-3"><p className="text-xs font-medium text-muted-foreground">Process orders</p><p className="mt-1 text-lg font-bold text-foreground">{report.summary.order_count}</p></CardContent></Card>
        <Card className="shadow-xs"><CardContent className="p-3"><p className="text-xs font-medium text-muted-foreground">GR postings</p><p className="mt-1 text-lg font-bold text-foreground">{report.summary.posting_count}</p></CardContent></Card>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <TooltipProvider delayDuration={250}>
            <TabsList className="h-auto flex-wrap">
              <Tooltip><TooltipTrigger asChild><TabsTrigger value="detail">Detail</TabsTrigger></TooltipTrigger><TooltipContent>Every included goods-receipt document item.</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><TabsTrigger value="daily">Daily Summary</TabsTrigger></TooltipTrigger><TooltipContent>Grouped by posting date, plant, material, and unit. Shows net quantity, orders, postings, and a running total.</TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild><TabsTrigger value="period">Weekly / Period Cumulative</TabsTrigger></TooltipTrigger><TooltipContent>Grouped by plant, material, and unit across the selected period. Shows cumulative quantity, target, and percentage of target.</TooltipContent></Tooltip>
            </TabsList>
          </TooltipProvider>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 border-slate-300 bg-background"
                  title="Choose Columns"
                  aria-label="Choose Columns"
                  data-testid="customize-columns-btn"
                >
                  <Columns className="w-4 h-4 text-blue-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2 rounded-xl shadow-xl border-slate-200">
                <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-slate-100">
                  <span className="text-xs font-bold text-slate-800">Display Columns</span>
                  <button type="button" onClick={resetDetailColumns} className="text-[10px] text-blue-600 hover:underline font-semibold">Reset</button>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-1 p-1">
                  {DETAIL.map(([key, title]) => (
                    <label key={key} className="flex items-center gap-2 px-2 py-1 rounded-md text-xs hover:bg-slate-100 cursor-pointer select-none">
                      <Checkbox
                        checked={visibleDetailKeys.includes(key)}
                        onCheckedChange={() => toggleDetailColumn(key)}
                      />
                      <span className="text-slate-700 font-medium">{title}</span>
                    </label>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border">
              <Button
                type="button"
                variant={viewMode === "table" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2.5 text-xs gap-1.5 font-medium"
                onClick={() => setViewMode("table")}
                data-testid="table-view-btn"
              >
                <Table className="w-3.5 h-3.5" /> Table View
              </Button>
              <Button
                type="button"
                variant={viewMode === "tiles" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2.5 text-xs gap-1.5 font-medium"
                onClick={() => setViewMode("tiles")}
                data-testid="tiles-view-btn"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Tiles View
              </Button>
            </div>
          </div>
        </div>
        <TabsContent value="detail">
          {viewMode === "table" ? (
            <ReportTable rows={report.documents} columns={activeDetailColumns} label="Production receipt detail" />
          ) : (
            <ReportTiles rows={report.documents} columns={activeDetailColumns} label="Production receipt detail" />
          )}
        </TabsContent>
        <TabsContent value="daily">
          {viewMode === "table" ? (
            <ReportTable rows={report.daily_summary} columns={DAILY} label="Daily production summary" />
          ) : (
            <ReportTiles rows={report.daily_summary} columns={DAILY} label="Daily production summary" />
          )}
        </TabsContent>
        <TabsContent value="period">
          <p className="mb-3 text-sm text-muted-foreground">Targets count each receiving process order once per material/unit. These are whole-order targets, not a weekly production plan. Missing or incompatible targets show —.</p>
          {viewMode === "table" ? (
            <ReportTable rows={report.cumulative} columns={PERIOD} label="Period cumulative production" />
          ) : (
            <ReportTiles rows={report.cumulative} columns={PERIOD} label="Period cumulative production" />
          )}
        </TabsContent>
      </Tabs>
      {!report.documents.length && <p role="status" className="text-center text-muted-foreground">No process-order receipts match these filters.</p>}
    </>}
  </div>}
  </div>;
}
